import { SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON } from '@/lib/constants';

export { SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON };

export interface TokenConfig {
  symbol: string;
  name: string;
  decimals: number;
  mintAddress: string;
  brandColor: string;
  brandColorMuted: string;
  brandColorBg: string;
  brandColorBorder: string;
}

export const SUPPORTED_TOKENS: Record<string, TokenConfig> = {
  [SOL]: {
    symbol: 'SOL',
    name: 'Solana',
    decimals: 9,
    mintAddress: SOL,
    brandColor: 'hsl(43 75% 68%)',
    brandColorMuted: 'hsl(38 65% 57%)',
    brandColorBg: 'hsl(38 65% 57% / 0.12)',
    brandColorBorder: 'hsl(38 65% 57% / 0.35)',
  },
  [BONK]: {
    symbol: 'BONK',
    name: 'Bonk',
    decimals: 5,
    mintAddress: BONK,
    brandColor: 'hsl(36 92% 55%)',
    brandColorMuted: 'hsl(36 85% 50%)',
    brandColorBg: 'hsl(36 85% 50% / 0.12)',
    brandColorBorder: 'hsl(36 85% 50% / 0.35)',
  },
  [SKR]: {
    symbol: 'SKR',
    name: 'Seeker',
    decimals: 6,
    mintAddress: SKR,
    brandColor: 'hsl(168 80% 48%)',
    brandColorMuted: 'hsl(168 70% 42%)',
    brandColorBg: 'hsl(168 70% 42% / 0.12)',
    brandColorBorder: 'hsl(168 70% 42% / 0.35)',
  },
  [MEW]: {
    symbol: 'MEW',
    name: 'cat in a dogs world',
    decimals: 5,
    mintAddress: MEW,
    brandColor: 'hsl(290 70% 68%)',
    brandColorMuted: 'hsl(290 60% 58%)',
    brandColorBg: 'hsl(290 60% 58% / 0.12)',
    brandColorBorder: 'hsl(290 60% 58% / 0.35)',
  },
  [USDC]: {
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    mintAddress: USDC,
    brandColor: 'hsl(214 72% 60%)',
    brandColorMuted: 'hsl(214 65% 52%)',
    brandColorBg: 'hsl(214 65% 52% / 0.12)',
    brandColorBorder: 'hsl(214 65% 52% / 0.35)',
  },
  [ANGELIC_SOL]: {
    symbol: 'ANGEL',
    name: 'AngelicSol',
    decimals: 6,
    mintAddress: ANGELIC_SOL,
    brandColor: 'hsl(280 80% 72%)',
    brandColorMuted: 'hsl(280 70% 60%)',
    brandColorBg: 'hsl(280 70% 60% / 0.12)',
    brandColorBorder: 'hsl(280 70% 60% / 0.35)',
  },
  [GMT]: {
    symbol: 'GMT',
    name: 'STEPN',
    decimals: 9,
    mintAddress: GMT,
    brandColor: 'hsl(142 65% 55%)',
    brandColorMuted: 'hsl(142 55% 45%)',
    brandColorBg: 'hsl(142 55% 45% / 0.12)',
    brandColorBorder: 'hsl(142 55% 45% / 0.35)',
  },
  [DRAGON]: {
    symbol: 'DRAGON',
    name: 'DRAGON',
    decimals: 6,
    mintAddress: DRAGON,
    brandColor: 'hsl(8 85% 58%)',
    brandColorMuted: 'hsl(8 75% 48%)',
    brandColorBg: 'hsl(8 75% 48% / 0.12)',
    brandColorBorder: 'hsl(8 75% 48% / 0.35)',
  },
};

export function getTokenByMint(mint: string | undefined | null): TokenConfig {
  if (!mint) return SUPPORTED_TOKENS[SOL];
  return SUPPORTED_TOKENS[mint] ?? SUPPORTED_TOKENS[SOL];
}

export function getDecimalsForMint(mint: string | undefined | null): number {
  return getTokenByMint(mint).decimals;
}

/**
 * Converts raw token units (lamports/base units) to human-readable amount.
 * Accepts number or bigint so callers can pass BigInt amounts without lossy conversion.
 * e.g. for SOL: 1_000_000_000 → 1.0
 * for BONK: 100_000 → 1.0
 */
export function formatTokenAmount(
  rawAmount: number | bigint,
  mint: string | undefined | null,
  displayDecimals?: number,
): string {
  const token = getTokenByMint(mint);
  const divisor = Math.pow(10, token.decimals);
  const humanAmount = Number(rawAmount) / divisor;
  const sym = token.symbol;

  if (humanAmount === 0) return `0 ${sym}`;

  // Auto-select display decimals based on token
  const dec = displayDecimals ?? (token.decimals <= 5 ? 0 : 4);

  if (humanAmount < 0.001) {
    return `${humanAmount.toFixed(6)} ${sym}`;
  }

  return `${humanAmount.toLocaleString('en-US', {
    minimumFractionDigits: Math.min(dec, 2),
    maximumFractionDigits: dec,
  })} ${sym}`;
}

/**
 * Expands a scientific-notation decimal string (e.g. "1.5e3", "1e+21", "2.5e-4")
 * into a plain decimal string without any exponent.
 * Returns the original string unchanged if no exponent is present.
 * Only used internally by parseTokenAmountBigInt.
 */
function expandScientificNotation(s: string): string {
  // Quick exit — no exponent present
  if (!/[eE]/.test(s)) return s;

  // Use Number to parse it, but we need to expand it without floating-point
  // truncation for large values. We do this with a pure string approach.
  const lower = s.toLowerCase();
  const eIdx = lower.indexOf('e');
  const mantissa = lower.slice(0, eIdx);
  const expStr = lower.slice(eIdx + 1);
  const exp = parseInt(expStr, 10);

  if (!isFinite(exp)) return '0';

  // Split mantissa into sign, integer part, fractional part
  let negative = false;
  let mant = mantissa;
  if (mant.startsWith('-')) { negative = true; mant = mant.slice(1); }
  else if (mant.startsWith('+')) { mant = mant.slice(1); }

  const dotIdx = mant.indexOf('.');
  let intDigits: string;
  let fracDigits: string;
  if (dotIdx === -1) {
    intDigits = mant || '0';
    fracDigits = '';
  } else {
    intDigits = mant.slice(0, dotIdx) || '0';
    fracDigits = mant.slice(dotIdx + 1);
  }

  // All digits concatenated (no decimal point yet), virtual dot is after intDigits
  const allDigits = intDigits + fracDigits;
  // The virtual dot position from the left in allDigits is intDigits.length
  // After applying the exponent, the new dot position is:
  let dotPos = intDigits.length + exp;

  let result: string;
  if (dotPos >= allDigits.length) {
    // Pure integer — pad with zeros on the right
    result = allDigits.padEnd(dotPos, '0');
  } else if (dotPos <= 0) {
    // Pure fraction starting with "0."
    result = '0.' + '0'.repeat(-dotPos) + allDigits;
  } else {
    // Mixed integer + fractional
    result = allDigits.slice(0, dotPos) + '.' + allDigits.slice(dotPos);
  }

  return (negative ? '-' : '') + result;
}

/**
 * Converts human-readable amount to raw token base units as a BigInt.
 * Uses string-based integer arithmetic to avoid floating-point precision loss.
 * e.g. for SOL: "1.5" → 1_500_000_000n
 * for BONK: "1000" → 100_000_000n
 *
 * Accepts a human amount as a number or decimal string. Passing a pre-parsed
 * number is fine for the magnitudes users can enter — the string path is used
 * internally to preserve sub-integer precision without float rounding.
 *
 * This function NEVER throws. It returns 0n for any invalid, empty, or
 * non-finite input (including scientific notation from mobile keyboards,
 * form-state restoration artifacts, etc.).
 */
export function parseTokenAmountBigInt(humanAmount: number | string | undefined | null, mint: string | undefined | null): bigint {
  try {
    const token = getTokenByMint(mint);
    const decimals = token.decimals;

    // Handle non-finite numbers up-front
    if (typeof humanAmount === 'number') {
      if (!isFinite(humanAmount) || isNaN(humanAmount)) return 0n;
    }

    // Coerce to string, strip whitespace; treat null/undefined as "0"
    let str = (humanAmount == null ? '' : String(humanAmount)).trim();

    if (str === '' || str === 'undefined' || str === 'null' || str === 'NaN' || str === 'Infinity' || str === '-Infinity') {
      return 0n;
    }

    // Expand scientific notation (e.g. "1e5" → "100000", "1.5e-2" → "0.015")
    str = expandScientificNotation(str);

    // Strip any characters that aren't digits, a leading "-", or a single "."
    // We allow one leading minus sign.
    const negative = str.startsWith('-');
    if (negative) str = str.slice(1);

    // Remove all characters except digits and the first "."
    const firstDot = str.indexOf('.');
    if (firstDot === -1) {
      str = str.replace(/[^0-9]/g, '');
    } else {
      const beforeDot = str.slice(0, firstDot).replace(/[^0-9]/g, '');
      const afterDot = str.slice(firstDot + 1).replace(/[^0-9]/g, '');
      str = beforeDot + '.' + afterDot;
    }

    if (str === '' || str === '.') return 0n;

    // Negative amounts are not meaningful for token deposits — treat as 0n
    if (negative) return 0n;

    // Split on decimal point
    const dotIdx = str.indexOf('.');
    let intPart: string;
    let fracPart: string;

    if (dotIdx === -1) {
      intPart = str || '0';
      fracPart = '';
    } else {
      intPart = str.slice(0, dotIdx) || '0';
      fracPart = str.slice(dotIdx + 1);
    }

    // intPart and fracPart are now guaranteed digit-only strings
    if (intPart === '') intPart = '0';

    // Truncate or zero-pad the fractional part to exactly `decimals` digits
    const fracPadded = fracPart.slice(0, decimals).padEnd(decimals, '0');

    // Combine: e.g. "1" + "500000000" for 1.5 SOL (9 decimals)
    const combined = intPart + fracPadded;

    // Final defensive wrap — BigInt() should never throw at this point since
    // combined is guaranteed to be a digit-only string, but just in case.
    return BigInt(combined);
  } catch {
    return 0n;
  }
}

/**
 * Converts human-readable amount to raw token units.
 * e.g. for SOL: 1.5 → 1_500_000_000
 * for BONK: 1000 → 100_000_000
 *
 * Returns a number. For policy-critical arithmetic (e.g. lkAmt = depAmt * 99 // 100),
 * use parseTokenAmountBigInt instead to avoid precision loss on large amounts.
 */
export function parseTokenAmount(humanAmount: number, mint: string | undefined | null): number {
  return Number(parseTokenAmountBigInt(humanAmount, mint));
}

/**
 * Formats just the numeric value without symbol.
 */
export function formatTokenValue(rawAmount: number, mint: string | undefined | null): string {
  const token = getTokenByMint(mint);
  const divisor = Math.pow(10, token.decimals);
  const humanAmount = rawAmount / divisor;
  const dec = token.decimals <= 5 ? 0 : 4;

  return humanAmount.toLocaleString('en-US', {
    minimumFractionDigits: Math.min(dec, 2),
    maximumFractionDigits: dec,
  });
}
